/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { Avatar } from "../../shared/Avatar";
import type { Member } from "../../Team/types";

type Props = { people: Member[]; hours: Map<number, number>; maxWeek: number };

// This week's planned hours per person against their contract and the weekly limit.
export const PeopleHours = defineComponent<Props>({
  name: "PeopleHours",
  props: ["people", "hours", "maxWeek"],
  setup(props) {
    const { t } = useLocalization();
    return () => (
      <div class="rounded-2xl border border-base-300 bg-base-100 p-4">
        <div class="mb-3 text-sm font-semibold">{t("workday.rota.hours_this_week")}</div>
        <ul class="space-y-2.5">
          {props.people.map((p) => {
            const planned = props.hours.get(p.id) ?? 0;
            const target = p.contract_hours_week ?? null;
            const pct = Math.min(100, (planned / (target || props.maxWeek)) * 100);
            const over = planned > props.maxWeek;
            return (
              <li key={p.id} class="flex items-center gap-2">
                <Avatar name={p.name} id={p.id} color={p.color} size="xs" />
                <div class="min-w-0 flex-1">
                  <div class="flex justify-between text-xs">
                    <span class="truncate">{p.name}</span>
                    <span class={["tabular-nums", over ? "font-semibold text-error" : "text-base-content/60"].join(" ")}>
                      {`${planned.toFixed(1)}h${target != null ? ` / ${target}h` : ""}`}
                    </span>
                  </div>
                  <div class="mt-1 h-1.5 overflow-hidden rounded-full bg-base-200">
                    <div class={["h-full rounded-full", over ? "bg-error" : target != null && planned > target ? "bg-warning" : "bg-primary"].join(" ")} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    );
  },
});
